import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useTranslations } from "@src/packages/i18n";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import type { MedicalHistoryReviewSectionKey } from "../sectionRegistry";

type MedicalHistoryQuestionContextValue = {
  sectionLabel?: string;
  helpText?: string;
  onClose?: () => void;
  /** Records a question screen becoming visible in the `medical_history` funnel. */
  stepViewed: (step: string) => void;
  /** Records a question screen being advanced past. */
  stepSubmitted: (step: string) => void;
  /** Records the section reaching its completion screen. */
  sectionCompleted: () => void;
};

const noop = () => {};

const MedicalHistoryQuestionContext = createContext<
  MedicalHistoryQuestionContextValue | undefined
>(undefined);

type MedicalHistoryQuestionProviderProps = {
  children: ReactNode;
  /**
   * Which of the seventeen sections this flow is. Travels as the `section`
   * property on every `medical_history_*` event, so one funnel covers all of
   * them and can be sliced per section instead of duplicated per section.
   */
  section?: MedicalHistoryReviewSectionKey;
  sectionLabel?: string;
  sectionLabelKey?: string;
  helpText?: string;
  helpTextKey?: string;
  onClose?: () => void;
};

/**
 * Supplies flow-level header metadata so every question gets the same
 * category chip and a useful help affordance without duplicating it in each
 * screen.
 *
 * It also owns the section's funnel. Every section navigator already renders
 * this provider around its stack, which makes it the one place that spans a
 * section from its first question to its completion screen — so `started` and
 * `abandoned` live here, and the screens below only have to say which step
 * they are.
 */
export function MedicalHistoryQuestionProvider({
  children,
  section,
  sectionLabel,
  sectionLabelKey,
  helpText,
  helpTextKey,
  onClose,
}: MedicalHistoryQuestionProviderProps) {
  const t = useTranslations();
  const flow = useFlowAnalytics("medical_history");
  // Read inside the unmount effect, which must not re-run when the section
  // finishes — a dependency on `completed` would fire `abandoned` early.
  const completedRef = useRef(false);

  useEffect(() => {
    if (!section) return;
    flow.started({ section });
    return () => {
      if (completedRef.current) return;
      // Leaving a section mid-way is the drop-off the funnel exists to show.
      // The answers already given are saved; the section is not finished.
      flow.abandoned("left_before_completing", { section });
    };
  }, [flow, section]);

  const value = useMemo<MedicalHistoryQuestionContextValue>(
    () => ({
      sectionLabel:
        sectionLabel ?? (sectionLabelKey ? t(sectionLabelKey) : undefined),
      helpText: helpText ?? (helpTextKey ? t(helpTextKey) : undefined),
      onClose,
      stepViewed: section ? (step) => flow.stepViewed(step, { section }) : noop,
      stepSubmitted: section
        ? (step) => flow.stepSubmitted(step, { section })
        : noop,
      sectionCompleted: section
        ? () => {
            completedRef.current = true;
            flow.completed({ section });
          }
        : noop,
    }),
    [
      flow,
      helpText,
      helpTextKey,
      onClose,
      section,
      sectionLabel,
      sectionLabelKey,
      t,
    ],
  );

  return (
    <MedicalHistoryQuestionContext.Provider value={value}>
      {children}
    </MedicalHistoryQuestionContext.Provider>
  );
}

export function useMedicalHistoryQuestionMetadata() {
  return useContext(MedicalHistoryQuestionContext);
}
